import React, { useState, useEffect } from "react";
import {
  FaCheck,
  FaCalendar,
  FaUser,
  FaMapMarkerAlt,
  FaSpinner,
  FaClock,
  FaRedo,
  FaDownload,
  FaCopy,
  FaEye,
  FaTimes,
  FaTag,
  FaPaperPlane,
  FaPlus,
  FaHourglassHalf,
  FaChartLine,
  FaInbox,
  FaArrowLeft,
  FaArrowRight,
  FaAd,
} from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DescriptionViewer from '../../Common/DescriptionViewer';
import AdsSubNav from '../../Common/AdsSubNav';
import CountdownTimer from '../../Common/CountdownTimer';

const InfluenerNewAdvertisement = () => {
  const navigate = useNavigate();
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ...existing code...
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [processingAdId, setProcessingAdId] = useState(null);
  const [confirmAdId, setConfirmAdId] = useState(null);
  const [isConfirmChecked, setIsConfirmChecked] = useState(false);
  const [downloadAdId, setDownloadAdId] = useState(null);
  const [isDownloadChecked, setIsDownloadChecked] = useState(false);
  const [showAdImage, setShowAdImage] = useState(false);
  const [adImageUrl, setAdImageUrl] = useState("");
  // Rejection modal state
  const [rejectAdId, setRejectAdId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isRejectChecked, setIsRejectChecked] = useState(false);
  // Advertisement preview modal state
  const [showAdPreview, setShowAdPreview] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);

  // Stats will be calculated after filtering
  const [stats, setStats] = useState({ newAds: 0 });

  const fetchAdvertisements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/new/ads`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("Advertisements response:", response);

      const advertisements = response.data?.data || response.data;
      setAdvertisements(Array.isArray(advertisements) ? advertisements : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching ads:", err);
      if (err.response && err.response.status === 403) {
        toast.error(
          err.response.data?.message ||
          "You are not a verified reporter. Please apply for your ID card first."
        );
      } else {
        setError("Failed to load advertisements. Please try again.");
        toast.error("Failed to load advertisements");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (confirmAdId || downloadAdId || showAdImage || rejectAdId || showAdPreview) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [confirmAdId, downloadAdId, showAdImage, rejectAdId, showAdPreview]);

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const handleAccept = async (adId) => {
    console.log("🔍 Frontend Accept Debug - Starting accept for adId:", adId);
    setProcessingAdId(adId);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/ad/accepted/${adId}`;
      console.log("🔍 Frontend Accept Debug - Request URL:", url);

      const res = await axios.put(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      console.log("🔍 Frontend Accept Debug - Accept response:", res);

      if (res.status === 200) {
        // Show success toast
        toast.success("Advertisement accepted successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Refresh the advertisements list
        await fetchAdvertisements();

        // Add a small delay to ensure toast is visible before navigation
        setTimeout(() => {
          navigate("/influencer/advertisement/accepted");
        }, 1500);
      }
    } catch (err) {
      console.error("🔍 Frontend Accept Debug - Error accepting ad:", err);
      console.error("🔍 Frontend Accept Debug - Error response:", err.response?.data);

      toast.error(
        err.response?.data?.message || "Failed to accept advertisement",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } finally {
      // Don't reset processingAdId immediately to show spinner during navigation
      setTimeout(() => {
        setProcessingAdId(null);
        setConfirmAdId(null);
        setIsConfirmChecked(false);
      }, 1500);
    }
  };

  const handleRejected = async (adId, reason) => {
    console.log("Rejecting advertisement:", adId, "Reason:", reason);
    setProcessingAdId(adId);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/ad/rejected/${adId}`,
        { note: reason },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      console.log("Reject response:", res);
      if (res.status === 200) {
        // Show success toast
        toast.success("Advertisement rejected successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Refresh the advertisements list
        await fetchAdvertisements();

        // Add a small delay to ensure toast is visible before navigation
        setTimeout(() => {
          navigate("/influencer/advertisement/rejected");
        }, 1500);
      }
    } catch (err) {
      console.error("Error rejecting advertisement:", err);
      toast.error(
        err.response?.data?.message || "Failed to reject advertisement",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } finally {
      // Don't reset processingAdId immediately to show spinner during navigation
      setTimeout(() => {
        setProcessingAdId(null);
        setRejectAdId(null);
        setRejectReason("");
        setIsRejectChecked(false);
      }, 1500);
    }
  };

  // Sort advertisements by startDate (or createdAt) descending
  const sortedAds = [...advertisements].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.startDate).getTime();
    const dateB = new Date(b.createdAt || b.startDate).getTime();
    return dateB - dateA;
  });

  const filteredAds = sortedAds.filter((ad) => {
    // Hide if acceptance window has passed (defensive re-check on frontend)
    const isExpired = ad.acceptBefore && new Date(ad.acceptBefore) < new Date();
    if (isExpired) return false;

    // Skip if ad is already fulfilled (defensive check)
    if (ad.acceptReporterCount >= ad.requiredReporter) return false;

    return true;
  });

  // Update stats whenever filtered ads change
  useEffect(() => {
    setStats({
      newAds: filteredAds.length
    });
  }, [advertisements, filteredAds.length]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const paginatedAds = filteredAds.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Copy description function
  const copyDescription = (description) => {
    navigator.clipboard.writeText(description).then(() => {
      toast.success("Description copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy description");
    });
  };

  // View advertisement function
  const viewAdvertisement = (ad) => {
    setSelectedAd(ad);
    setShowAdPreview(true);
  };

  // Close advertisement preview
  const closeAdPreview = () => {
    setShowAdPreview(false);
    setSelectedAd(null);
  };

  // Copy advertisement details
  const copyAdDetails = () => {
    if (selectedAd) {
      navigator.clipboard.writeText(selectedAd.mediaDescription).then(() => {
        toast.success("Description copied to clipboard!");
      }).catch(() => {
        toast.error("Failed to copy description");
      });
    }
  };

  const handleDownload = async (ad) => {
    try {
      const url =
        ad.mediaType?.toLowerCase() === "video" ? ad.videoUrl : ad.imageUrl;
      const fileName = `advertisement_${ad._id}.${ad.mediaType?.toLowerCase() === "video" ? "mp4" : "jpg"
        }`;

      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Advertisement downloaded successfully!");
    } catch (err) {
      console.error("Error downloading advertisement:", err);
      toast.error("Failed to download advertisement");
    } finally {
      setDownloadAdId(null);
      setIsDownloadChecked(false);
    }
  };

  // Function to handle showing the ad image popup
  const handleShowAdImage = (ad) => {
    if (ad.mediaType?.toLowerCase() === "video") {
      setAdImageUrl(ad.videoUrl);
    } else {
      setAdImageUrl(ad.imageUrl);
    }
    setShowAdImage(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Modern Header - Ultra Compact */}
      <div className="max-w-7xl mx-auto mb-3 sm:mb-4 px-2 sm:px-4">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-xl sm:rounded-[1.5rem] shadow-lg p-2.5 sm:p-4 text-white">
          <div className="flex flex-row items-center justify-between gap-2 sm:gap-4">

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate(-1)}
                className="hidden sm:flex px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] sm:text-xs font-black uppercase tracking-wider border border-white/20 transition-all whitespace-nowrap"
              >
                ← Back
              </button>
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg backdrop-blur-sm shadow-inner flex-shrink-0">
                <FaAd className="text-[12px] sm:text-base text-white" />
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-xs sm:text-lg font-black tracking-tight leading-none mb-0.5 sm:mb-1">New Ads</h1>
                <p className="text-blue-100 text-[8px] sm:text-[10px] font-bold uppercase tracking-widest leading-none">Available: {stats.newAds}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:flex flex-col items-end pr-3 border-r border-white/20">
                <div className="text-sm sm:text-base font-black leading-none">{stats.newAds}</div>
                <div className="text-[8px] sm:text-[9px] text-blue-100 uppercase tracking-widest font-bold">Opportunities</div>
              </div>
              <button
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition-all backdrop-blur-md text-[10px] sm:text-xs tracking-wider font-black uppercase shadow-lg border border-white/10 active:scale-95 group"
                onClick={fetchAdvertisements}
                disabled={loading}
              >
                <FaRedo className={`w-3 h-3 group-hover:rotate-180 transition-transform duration-500 ${loading ? "animate-spin" : ""}`} />
                <span>Refresh</span>
              </button>
            </div>

          </div>
        </div>
      </div>

      <AdsSubNav type="paid" />

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center py-6 sm:py-8 md:py-12">
          <FaSpinner className="animate-spin text-blue-500 text-2xl sm:text-3xl md:text-4xl" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg mb-3 sm:mb-4 md:mb-6 mx-2 sm:mx-4 text-xs sm:text-sm">
          <p>{error}</p>
        </div>
      )}

      {/* Advertisement Cards Grid */}
      {!loading && !error && (
        <div className="max-w-7xl mx-auto px-1 sm:px-4 md:px-6 lg:px-8">
          {filteredAds.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
              {paginatedAds.map((ad, index) => (
                <motion.div
                  key={ad._id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                  className="bg-white rounded-[1.5rem] border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden group hover:border-indigo-200 transition-all flex flex-col"
                >
                  {/* Media Top Section */}
                  <div className="relative w-full h-40 sm:h-48 md:h-36 overflow-hidden bg-slate-900 flex-shrink-0">
                    {ad.mediaType && ad.mediaType.toLowerCase() === "video" ? (
                      <video
                        src={ad.videoUrl}
                        className="w-full h-full object-cover opacity-80"
                      />
                    ) : (
                      <img
                        src={ad.imageUrl}
                        alt="ad"
                        className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-2 md:p-3">
                      <div className="flex items-center gap-1 bg-white/10 backdrop-blur-md px-1.5 py-0.5 rounded-lg border border-white/10">
                        <FaClock className="text-white text-[8px] md:text-[10px]" />
                        <span className="text-white text-[8px] md:text-[10px] font-bold">{ad.adLength}s</span>
                      </div>
                    </div>
                    {/* Floating Play/View Icon for mobile */}
                    <button
                      onClick={() => handleShowAdImage(ad)}
                      className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-md rounded-full border border-white/20 text-white">
                        <FaEye />
                      </div>
                    </button>
                  </div>

                  {/* Right Side (Mobile Details) / Bottom (Desktop Details) */}
                  <div className="flex-1 p-2 sm:p-3 flex flex-col justify-between min-w-0">
                    <div className="space-y-1.5 md:space-y-2">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h3 className="text-slate-900 font-black text-xs sm:text-base tracking-tight truncate leading-tight flex items-center gap-1.5 flex-wrap">
                            {ad.adType}
                            {(ad.platforms?.length > 0 || ad.plateforms?.length > 0 || ad.channelType?.length > 0) && (
                              <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border border-blue-100 whitespace-nowrap">
                                <span className="text-slate-900 font-[1000]">Post Type:</span> {ad.platforms?.[0] || ad.plateforms?.[0] || ad.channelType?.[0]}
                              </span>
                            )}
                          </h3>
                          <p className="text-slate-400 text-[8px] md:text-[9px] font-bold uppercase tracking-wider font-mono truncate">
                            ID: {ad._id.slice(-8)}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-emerald-600 text-xs md:text-lg font-[1000] leading-none">
                            ₹{Number(ad.finalReporterPrice).toFixed(0)}
                          </div>
                          <div className="text-[7px] md:text-[9px] font-black text-slate-300 uppercase">Earning</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <div className={`p-1 ${new Date(ad.acceptBefore) < new Date() ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'} rounded-md text-[9px] md:text-[10px]`}>
                          <FaClock />
                        </div>
                        <span className="text-[10px] md:text-[11px]">
                          <CountdownTimer
                            targetDate={ad.acceptBefore}
                            prefix="Ends in: "
                            expiredText="EXPIRED"
                          />
                        </span>
                      </div>
                    </div>

                    {/* Requirement & Slot Details Section */}
                    <div className="bg-slate-50/50 rounded-xl p-2 sm:p-3 border border-slate-100 flex flex-col gap-2">
                      <div className="flex justify-between items-center text-[10px] sm:text-xs">
                        <div className="flex items-center gap-1.5 text-slate-500 font-bold uppercase tracking-tight">
                          <FaChartLine className="text-indigo-500" /> Required Views
                        </div>
                        <div className="font-[1000] text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-lg border border-indigo-100">
                          {ad.baseView || "0"}
                        </div>
                      </div>

                      <div className="h-px bg-slate-100 w-full"></div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1.5 text-slate-500 font-bold text-[10px] uppercase tracking-tight">
                          <FaUser className="text-blue-500 shadow-sm" /> Slots
                        </div>
                        <div className="flex items-center gap-1.5 overflow-hidden">
                          <div className="flex flex-col items-center">
                            <span className="text-[7px] text-slate-400 font-black uppercase">Total</span>
                            <span className="text-[11px] font-black text-slate-700">{ad.requiredReporter || 0}</span>
                          </div>
                          <div className="w-[1px] h-4 bg-slate-200"></div>
                          <div className="flex flex-col items-center">
                            <span className="text-[7px] text-emerald-500 font-black uppercase text-[6px]">Filled</span>
                            <span className="text-[11px] font-black text-emerald-600">{ad.acceptReporterCount || 0}</span>
                          </div>
                          <div className="w-[1px] h-4 bg-slate-200"></div>
                          <div className="flex flex-col items-center">
                            <span className="text-[7px] text-orange-500 font-black uppercase px-1">Left</span>
                            <span className="text-[11px] font-black text-orange-600">
                              {Math.max(0, (ad.requiredReporter || 0) - (ad.acceptReporterCount || 0))}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group/desc relative cursor-pointer" onClick={() => viewAdvertisement(ad)}>
                      <div className="bg-slate-50 px-2 py-1.5 rounded-lg text-slate-500 text-[10px] font-bold italic line-clamp-1 pr-6 group-hover/desc:bg-indigo-50 transition-colors">
                        "{ad.mediaDescription}"
                      </div>
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-400 group-hover/desc:text-indigo-600 transition-colors">
                        <FaEye size={12} />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons - Stacked Column */}
                  <div className="flex flex-col gap-2 mt-4">
                    <button
                      onClick={() => setConfirmAdId(ad._id)}
                      disabled={processingAdId === ad._id}
                      className="w-full group relative bg-gradient-to-br from-indigo-600 to-violet-700 text-white py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-95 shadow-lg shadow-indigo-200 hover:shadow-indigo-300 overflow-hidden disabled:opacity-50"
                    >
                      <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 skew-x-[-20deg]" />
                      {processingAdId === ad._id ? (
                        <FaSpinner className="animate-spin mx-auto text-[10px]" />
                      ) : (
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          Accept Lead <FaPaperPlane className="w-2.5 h-2.5" />
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setRejectAdId(ad._id)}
                      className="w-full py-3 bg-white hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-2xl transition-all border border-slate-100 shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center gap-2 group/reject font-black text-[10px] uppercase tracking-widest"
                    >
                      <FaTimes className="group-hover/reject:rotate-90 transition-transform" />
                      Reject Lead
                    </button>
                  </div>

                  {/* Expiration Glow Bar */}
                  <div className="absolute top-0 right-0 w-12 h-12 bg-rose-500/5 blur-xl group-hover:bg-rose-500/10 pointer-events-none" />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="max-w-xl mx-auto text-center py-20 px-4">
              {/* Professional Empty State */}
              <div className="relative inline-block mb-10">
                <div className="absolute inset-0 bg-indigo-500/10 blur-[60px] rounded-full" />
                <div className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-[2.5rem] border-2 border-slate-100 shadow-2xl overflow-hidden mx-auto bg-slate-50 flex items-center justify-center group hover:rotate-6 transition-transform duration-500">
                  <FaInbox className="text-slate-200 text-6xl sm:text-7xl group-hover:scale-110 transition-transform" />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-slate-200" />
                    <div className="w-2 h-2 rounded-full bg-slate-200/50" />
                  </div>
                </div>
              </div>

              <h3 className="text-slate-900 font-black text-2xl tracking-tight mb-3">No Leads Available</h3>
              <p className="text-slate-500 text-sm font-medium leading-relaxed mb-10 max-w-sm mx-auto">
                No new advertisements are currently available for your location or category. Check back later for new opportunities!
              </p>
              <button
                onClick={fetchAdvertisements}
                className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white px-10 py-4 rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] transition-all hover:shadow-[0_20px_40px_rgba(79,70,229,0.3)] hover:-translate-y-1 active:scale-95 overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-[-20deg]" />
                <FaRedo className={`relative z-10 ${loading ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
                <span className="relative z-10">Scan for Leads</span>
              </button>
            </div>
          )}
        </div>
      )
      }

      {/* Pagination Controls */}
      {
        filteredAds.length > rowsPerPage && (
          <div className="flex justify-center mt-4 sm:mt-6 md:mt-8 px-2 sm:px-4">
            <nav className="inline-flex items-center space-x-1 sm:space-x-2 bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-200 p-1.5 sm:p-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-black uppercase tracking-widest transition-all duration-300 ${currentPage === 1
                  ? 'bg-slate-50 text-slate-300 cursor-not-allowed border border-slate-100'
                  : 'bg-white text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 hover:border-indigo-200 shadow-sm hover:shadow-md'
                  }`}
              >
                <span className="flex items-center gap-2">
                  <FaArrowLeft className="w-2.5 h-2.5" />
                  <span className="hidden sm:inline">Prev</span>
                </span>
              </button>

              <div className="flex items-center space-x-1 sm:space-x-1.5 px-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  const isActive = pageNum === currentPage;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl text-xs sm:text-sm font-black transition-all duration-300 ${isActive
                        ? 'bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-200 scale-105'
                        : 'bg-white text-slate-500 hover:bg-slate-50 border border-slate-200 hover:border-slate-300'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-black uppercase tracking-widest transition-all duration-300 ${currentPage === totalPages
                  ? 'bg-slate-50 text-slate-300 cursor-not-allowed border border-slate-100'
                  : 'bg-white text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 hover:border-indigo-200 shadow-sm hover:shadow-md'
                  }`}
              >
                <span className="flex items-center gap-2">
                  <span className="hidden sm:inline">Next</span>
                  <FaArrowRight className="w-2.5 h-2.5" />
                </span>
              </button>
            </nav>
          </div>
        )
      }

      {/* Advertisement Preview Modal */}
      {
        showAdPreview && selectedAd && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto bg-slate-900/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden relative"
            >
              {/* Modal Header */}
              <div className="bg-slate-900 px-6 py-6 sm:px-10 flex justify-between items-center text-white">
                <div>
                  <h2 className="text-xl font-[1000] tracking-tight">Lead Intelligence</h2>
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Detailed View</p>
                </div>
                <button onClick={closeAdPreview} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all">
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="p-6 sm:p-10 max-h-[70vh] overflow-y-auto custom-scrollbar">
                {/* Media Section */}
                <div className="rounded-[2rem] overflow-hidden bg-slate-900 mb-8 shadow-inner">
                  {selectedAd.mediaType?.toLowerCase() === "video" ? (
                    <video src={selectedAd.videoUrl} controls className="w-full max-h-[300px] mx-auto" />
                  ) : (
                    <img src={selectedAd.imageUrl} className="w-full h-auto object-contain max-h-[400px]" alt="Ad" />
                  )}
                </div>

                {/* BIG SIZE DESCRIPTION */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-slate-900 font-[1000] text-lg tracking-tight">Campaign Brief</h3>
                    <button onClick={copyAdDetails} className="flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:text-indigo-800">
                      <FaCopy /> Copy Text
                    </button>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-[2rem] p-6 sm:p-8">
                    <div className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed font-bold italic">
                      <DescriptionViewer
                        description={selectedAd.mediaDescription || ""}
                        textSize="text-base sm:text-lg md:text-xl"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )
      }

      {/* Download Confirmation Modal */}
      {
        downloadAdId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-md mx-4 text-center relative">
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-800">
                Confirm Download
              </h2>
              <p className="mb-3 sm:mb-4 md:mb-6 text-xs sm:text-sm md:text-base text-gray-600">
                Do you want to download this advertisement?
              </p>

              <div className="flex items-center justify-center mb-3 sm:mb-4 md:mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDownloadChecked}
                    onChange={(e) => setIsDownloadChecked(e.target.checked)}
                    className="mr-2 sm:mr-3 h-3 w-3 sm:h-4 sm:w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">
                    I confirm this download
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <button
                  onClick={() => {
                    const ad = filteredAds.find((ad) => ad._id === downloadAdId);
                    handleDownload(ad);
                  }}
                  className={`flex-1 px-8 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${isDownloadChecked
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-200 hover:shadow-blue-300 active:scale-95"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                  disabled={!isDownloadChecked}
                >
                  Confirm Download
                </button>
                <button
                  onClick={() => {
                    setDownloadAdId(null);
                    setIsDownloadChecked(false);
                  }}
                  className="px-8 py-3.5 bg-white text-slate-500 border border-slate-200 rounded-2xl hover:bg-slate-50 font-black text-[10px] uppercase tracking-widest transition-all"
                >
                  Cancel
                </button>
              </div>
              <button
                className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 text-gray-400 hover:text-gray-600 text-xl sm:text-2xl"
                onClick={() => {
                  setDownloadAdId(null);
                  setIsDownloadChecked(false);
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>
        )
      }

      {/* Reject Confirmation Modal */}
      {
        rejectAdId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-md mx-4 text-center relative">
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-800">
                Did you want to reject this ad?
              </h2>
              <p className="mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm md:text-base text-gray-600">
                Please provide a reason for rejection:
              </p>
              <textarea
                className="w-full border border-gray-300 rounded-lg p-2 mb-2 sm:mb-3 md:mb-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs sm:text-sm"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
              />
              <div className="flex items-center justify-center mb-3 sm:mb-4 md:mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isRejectChecked}
                    onChange={(e) => setIsRejectChecked(e.target.checked)}
                    className="mr-2 sm:mr-3 h-3 w-3 sm:h-4 sm:w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">
                    I confirm this rejection
                  </span>
                </label>
              </div>
              <div className="flex justify-center gap-2 sm:gap-3 md:gap-4">
                <button
                  onClick={() => handleRejected(rejectAdId, rejectReason)}
                  className={`flex-1 px-8 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${isRejectChecked && rejectReason.trim()
                    ? "bg-gradient-to-r from-rose-600 to-red-700 text-white shadow-lg shadow-rose-200 hover:shadow-rose-300 active:scale-95"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                  disabled={
                    processingAdId === rejectAdId ||
                    !isRejectChecked ||
                    !rejectReason.trim()
                  }
                >
                  {processingAdId === rejectAdId
                    ? "Processing..."
                    : "Confirm Rejection"}
                </button>
                <button
                  onClick={() => {
                    setRejectAdId(null);
                    setRejectReason("");
                    setIsRejectChecked(false);
                  }}
                  className="px-8 py-3.5 bg-white text-slate-500 border border-slate-200 rounded-2xl hover:bg-slate-50 font-black text-[10px] uppercase tracking-widest transition-all"
                  disabled={processingAdId === rejectAdId}
                >
                  Cancel
                </button>
              </div>
              <button
                className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 text-gray-400 hover:text-gray-600 text-xl sm:text-2xl"
                onClick={() => {
                  setRejectAdId(null);
                  setRejectReason("");
                  setIsRejectChecked(false);
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>
        )
      }
      {
        confirmAdId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-md mx-4 text-center relative">
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-800">
                Confirm Acceptance
              </h2>
              <p className="mb-3 sm:mb-4 md:mb-6 text-xs sm:text-sm md:text-base text-gray-600">
                Are you sure you want to accept this advertisement?
              </p>

              <div className="flex items-center justify-center mb-3 sm:mb-4 md:mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isConfirmChecked}
                    onChange={(e) => setIsConfirmChecked(e.target.checked)}
                    className="mr-2 sm:mr-3 h-3 w-3 sm:h-4 sm:w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">
                    I understand and confirm this acceptance
                  </span>
                </label>
              </div>

              <div className="flex justify-center gap-2 sm:gap-3 md:gap-4">
                {/* <button
                onClick={() => {
                 
                }}
                className="px-6 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded-lg font-semibold transition"
              >
                View Ad..
              </button> */}
                <button
                  onClick={() => {
                    handleAccept(confirmAdId);
                    const ad = filteredAds.find((ad) => ad._id === confirmAdId);
                    handleDownload(ad);
                  }}
                  className={`flex-1 px-8 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${isConfirmChecked
                    ? "bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-lg shadow-emerald-200 hover:shadow-emerald-300 active:scale-95"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                  disabled={processingAdId === confirmAdId || !isConfirmChecked}
                >
                  {processingAdId === confirmAdId
                    ? "Processing..."
                    : "Confirm Acceptance"}
                </button>
                <button
                  onClick={() => {
                    setConfirmAdId(null);
                    setIsConfirmChecked(false);
                  }}
                  className="px-8 py-3.5 bg-white text-slate-500 border border-slate-200 rounded-2xl hover:bg-slate-50 font-black text-[10px] uppercase tracking-widest transition-all"
                  disabled={processingAdId === confirmAdId}
                >
                  Cancel
                </button>
              </div>
              <button
                className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 text-gray-400 hover:text-gray-600 text-xl sm:text-2xl"
                onClick={() => {
                  setConfirmAdId(null);
                  setIsConfirmChecked(false);
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>
        )
      }

      {/* Ad Image/Video Popup */}
      {
        showAdImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg p-6 relative max-w-lg w-full flex flex-col items-center">
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl"
                onClick={() => setShowAdImage(false)}
                aria-label="Close"
              >
                &times;
              </button>
              <h2 className="text-lg font-bold mb-4">Advertisement Preview</h2>
              {adImageUrl ? (
                adImageUrl.match(/\.(mp4|webm|ogg)$/i) ? (
                  <video
                    src={adImageUrl}
                    controls
                    className="max-h-96 max-w-full rounded"
                  />
                ) : (
                  <img
                    src={adImageUrl}
                    alt="Ad"
                    className="max-h-96 max-w-full rounded"
                  />
                )
              ) : (
                <div className="text-gray-500">No media available</div>
              )}
            </div>
          </div>
        )
      }

      {/* Advertisement Preview Modal */}
      {
        showAdPreview && selectedAd && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 relative max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl z-10"
                onClick={closeAdPreview}
                aria-label="Close"
              >
                <FaTimes />
              </button>

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Advertisement Preview</h2>
                <p className="text-gray-600">Complete details of this advertising opportunity</p>
              </div>

              {/* Advertisement Media */}
              <div className="mb-6">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  {selectedAd.mediaType?.toLowerCase() === "video" ? (
                    <video
                      src={selectedAd.videoUrl}
                      controls
                      className="w-full max-h-96 rounded-lg"
                    />
                  ) : (
                    <img
                      src={selectedAd.imageUrl}
                      alt="Advertisement"
                      className="w-full max-h-96 object-contain rounded-lg"
                    />
                  )}
                </div>
              </div>

              {/* Advertisement Description */}
              <div className="mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">Description</h3>
                <div className="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200">
                  <DescriptionViewer
                    description={selectedAd.mediaDescription || ""}
                    wordLimit={10}
                    textSize="text-sm sm:text-base"
                  />
                </div>
              </div>

              {/* Advertisement Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">💰 Your Reward</h4>
                  <p className="text-2xl font-bold text-green-600">₹{selectedAd.finalReporterPrice}</p>
                  <p className="text-sm text-green-600">Earn this amount for completing the task</p>
                </div>

                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <h4 className="font-semibold text-purple-800 mb-2">👁️ Required Views</h4>
                  <p className="text-2xl font-bold text-purple-600">{selectedAd.baseView || "0"}</p>
                  <p className="text-sm text-purple-600">Total views required to complete task</p>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">⏱️ Duration</h4>
                  <p className="text-xl font-bold text-blue-600">{selectedAd.adLength} seconds</p>
                  <p className="text-sm text-blue-600">Advertisement length</p>
                </div>

                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                  <h4 className="font-semibold text-orange-800 mb-2">📍 Location</h4>
                  <p className="text-lg font-bold text-orange-600">
                    {selectedAd.pfState || "Not specified"}
                  </p>
                  {selectedAd.pfCities && selectedAd.pfCities.length > 0 && (
                    <p className="text-sm text-orange-600 mt-1">
                      {selectedAd.pfCities.join(', ')}
                    </p>
                  )}
                  <p className="text-xs text-orange-500 mt-1">Target location</p>
                </div>

                <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
                  <h4 className="font-semibold text-cyan-800 mb-2">👥 Reporter Slots</h4>
                  <div className="flex gap-4">
                    <div>
                      <p className="text-xs text-cyan-600 uppercase font-bold">Total</p>
                      <p className="text-lg font-bold text-cyan-700">{selectedAd.requiredReporter || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-cyan-600 uppercase font-bold">Left</p>
                      <p className="text-lg font-bold text-rose-600">{Math.max(0, (selectedAd.requiredReporter || 0) - (selectedAd.acceptReporterCount || 0))}</p>
                    </div>
                  </div>
                </div>

                {selectedAd.platforms && selectedAd.platforms.length > 0 && (
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                    <h4 className="font-semibold text-indigo-800 mb-2">📱 Platforms</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedAd.platforms.map((platform, idx) => (
                        <span key={idx} className="bg-indigo-100 text-indigo-800 text-sm px-3 py-1 rounded-full font-medium">
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Timeline Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Timeline</h3>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Expires On</p>
                      <p className="font-semibold text-gray-800">
                        {new Date(selectedAd.endDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Created</p>
                      <p className="font-semibold text-gray-800">
                        {new Date(selectedAd.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <button
                  onClick={copyAdDetails}
                  className="flex-1 inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-2xl hover:shadow-xl transition-all active:scale-95 border border-white/10"
                >
                  <FaCopy className="mr-2" />
                  Copy Text
                </button>
                <button
                  onClick={() => {
                    closeAdPreview();
                    setConfirmAdId(selectedAd._id);
                  }}
                  className="flex-1 inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-700 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-2xl hover:shadow-xl transition-all active:scale-95 shadow-lg shadow-emerald-200"
                >
                  <FaCheck className="mr-2" />
                  Accept Lead
                </button>
              </div>
            </div>
          </div>
        )
      }

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div >
  );
};

export default InfluenerNewAdvertisement;
